import { useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import { useProject } from '@contexts/Project.context';
import { fetchUrl } from '@utils/api';
import { getLayerByCustomId } from '@utils/map/helpers';

const ColorBarContainer = styled.div`
	/* Laid out by MapControlsCluster; it used to hardcode its own bottom offset,
	   which drifted away from the rest of the corner when the carousel opened. */
	pointer-events: auto;
	background: ${props => props.theme.colors.background};
	border: 2px solid ${props => props.theme.colors.border || '#333'};
	border-radius: 8px;
	padding: 12px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
	min-width: 140px;
	font-family: Arial, sans-serif;
`;

const ColorBarTitle = styled.div`
	font-weight: bold;
	font-size: 14px;
	margin-bottom: 8px;
	text-align: center;
	color: ${props => props.theme.colors.textColor};
`;

const ColorBarContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const ColorClass = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 12px;
	color: ${props => props.theme.colors.textColor};
`;

const ColorBox = styled.div`
	width: 40px;
	height: 20px;
	background-color: ${props => props.$color};
	border: 1px solid ${props => props.theme.colors.border || '#666'};
	border-radius: 2px;
	opacity: ${props => (props.$transparent ? 0.3 : 1)};
`;

const Label = styled.div`
	text-align: left;
	white-space: nowrap;
	flex: 1;
`;

// const ColorBarDescription = styled.div`
// 	font-size: 11px;
// 	color: ${props => props.theme.colors.textAccent || '#666'};
// 	margin-top: 8px;
// 	text-align: center;
// `;

const LoadingText = styled.div`
	font-size: 12px;
	color: ${props => props.theme.colors.textAccent};
	text-align: center;
`;

// Polling interval for checking layer visibility (in milliseconds)
const LAYER_VISIBILITY_CHECK_INTERVAL = 2000;

/**
 * Normalizes color values from various formats to valid CSS strings
 *
 * @param {string|Array|Object} value - Color value in various formats:
 *   - Array: [r, g, b, a] e.g., [255, 0, 0, 1]
 *   - Object: {r, g, b, a} e.g., {r: 255, g: 0, b: 0, a: 1}
 *   - String: 'rgb(255,0,0)' or '#ff0000'
 * @returns {string|null} Valid CSS color string (e.g., 'rgba(255,0,0,1)') or null
 *
 * @example
 * normalizeColor([255, 0, 0, 1]) // returns 'rgba(255,0,0,1)'
 * normalizeColor({r: 255, g: 0, b: 0, a: 1}) // returns 'rgba(255, 0, 0, 1)'
 * normalizeColor('#ff0000') // returns '#ff0000'
 */
const normalizeColor = value => {
	if (!value) return null;

	// Handle array format: [r, g, b, a]
	if (Array.isArray(value)) {
		if (value.length === 3) return `rgb(${value.join(',')})`;
		if (value.length >= 4) return `rgba(${value.slice(0, 4).join(',')})`;
		return null;
	}

	// Handle object format: {r, g, b, a}
	if (typeof value === 'object') {
		const { r, g, b, a = 1 } = value;
		const hasRequiredComponents = [r, g, b].every(v => v !== undefined);

		if (hasRequiredComponents) {
			return `rgba(${r}, ${g}, ${b}, ${a})`;
		}
	}

	// Return string format as-is (hex, rgb, rgba, etc.)
	return value;
};

/**
 * CHM ColorBar Legend Component
 *
 * Displays a dynamic color-coded legend for the Canopy Height Model (CHM) layer.
 * The legend shows tree height classifications with corresponding colors fetched
 * from the project's colormap configuration file.
 *
 * Features:
 * - Automatically shows/hides based on CHM layer visibility
 * - Fetches colormap data from S3 storage
 * - Supports multiple color formats (hex, rgba array, rgba object)
 * - Displays loading state during data fetch
 * - Handles transparent colors for ground level (0-1m)
 *
 * @component
 * @returns {JSX.Element|null} Color bar legend component or null if layer is not visible
 */
const CHMColorBar = () => {
	const { project, mapObject } = useProject();
	const [isVisible, setIsVisible] = useState(false);
	const { uuid } = project || {};

	// Fetch colormap data from S3 storage
	// Only fetches when the CHM layer is visible to optimize performance
	const {
		data: colormap,
		isLoading,
		error,
	} = useQuery({
		queryKey: ['chmColormap', uuid],
		queryFn: async () => {
			// Step 1: Get signed URL for the colormap JSON file
			const data = await fetchUrl(`fileLink?key=${uuid}/odm_orthophoto/chm_colormap.json`);

			if (data?.url) {
				// Step 2: Fetch the actual colormap data from the signed URL
				const response = await fetch(data.url);

				if (!response.ok) {
					throw new Error(
						`Failed to fetch colormap: ${response.status} ${response.statusText}`
					);
				}

				return response.json();
			}

			throw new Error('No colormap URL found');
		},
		enabled: !!uuid && isVisible, // Only fetch when we have a project UUID and layer is visible
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		staleTime: Infinity, // Cache indefinitely as colormap rarely changes
		retry: 1,
	});

	// Monitor CHM layer visibility with polling
	// Note: OpenLayers doesn't provide a built-in visibility change event,
	// so we use polling as a workaround
	useEffect(() => {
		if (!mapObject) return;

		const checkLayerVisibility = () => {
			const chmLayer = getLayerByCustomId(mapObject, 'colored_chm');

			if (chmLayer) {
				setIsVisible(chmLayer.getVisible());
			} else {
				setIsVisible(false);
			}
		};

		// Check visibility immediately on mount
		checkLayerVisibility();

		// Set up polling to check visibility periodically
		const interval = setInterval(checkLayerVisibility, LAYER_VISIBILITY_CHECK_INTERVAL);

		// Cleanup: stop polling when component unmounts
		return () => clearInterval(interval);
	}, [mapObject]);

	// Parse and memoize height classes from colormap data
	// This ensures we only reprocess when the colormap data actually changes
	const heightClasses = useMemo(() => {
		// Early return if we don't have valid data
		if (!colormap || !isVisible || isLoading || error) return null;

		// Extract the steps array from various possible data structures
		let stepsArray = null;

		if (Array.isArray(colormap)) {
			// Case 1: Colormap is already an array
			stepsArray = colormap;
		} else if (colormap.steps) {
			// Case 2: Colormap has a 'steps' property
			stepsArray = colormap.steps;
		} else {
			// Case 3: Search through all object keys to find the array
			// This handles cases where the data structure is wrapped or uses different property names
			const keys = Object.keys(colormap);

			for (const key of keys) {
				const value = colormap[key];
				const isValidStepsArray =
					Array.isArray(value) &&
					value.length > 0 &&
					typeof value[0] === 'object' &&
					'height_min' in value[0];

				if (isValidStepsArray) {
					stepsArray = value;
					break;
				}
			}
		}

		// Validate we found a valid steps array
		if (!stepsArray || !Array.isArray(stepsArray) || stepsArray.length === 0) {
			return null;
		}

		// Transform each step into a display-ready height class
		return stepsArray.map(entry => {
			const { height_min, height_max, label, color, color_rgba, hex } = entry;

			// Normalize color to valid CSS (prefer hex > color_rgba > color)
			const displayColor = normalizeColor(hex || color_rgba || color);

			// Determine if this class should be rendered as transparent
			// Matches: 'transparent', rgba with alpha=0, rgba with alpha=0.0, etc.
			const isTransparent =
				entry.transparent === true ||
				displayColor === 'transparent' ||
				/^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*0(\.0+)?\s*\)$/.test(displayColor || '');

			// Format the height range label
			const range = label || `${height_min}-${height_max}m`;

			return {
				range,
				color: displayColor || 'transparent',
				transparent: isTransparent,
			};
		});
	}, [colormap, isVisible, isLoading, error]);

	// Don't render anything if the CHM layer is not visible
	if (!isVisible) return null;

	// Show loading state while fetching colormap data
	if (isLoading) {
		return (
			<ColorBarContainer>
				<LoadingText>Loading colormap...</LoadingText>
			</ColorBarContainer>
		);
	}

	// Don't render if there was an error or no data
	if (error || !colormap) {
		return null;
	}

	// Don't render if we couldn't parse valid height classes
	if (!heightClasses || heightClasses.length === 0) {
		return null;
	}

	// Render the color bar legend
	return (
		<ColorBarContainer>
			<ColorBarTitle>Height Classes</ColorBarTitle>

			<ColorBarContent>
				{heightClasses.map((heightClass, index) => (
					<ColorClass key={index}>
						<ColorBox
							$color={heightClass.color}
							$transparent={heightClass.transparent}
						/>
						<Label>{heightClass.range}</Label>
					</ColorClass>
				))}
			</ColorBarContent>
		</ColorBarContainer>
	);
};

export default CHMColorBar;
